// frontend/src/pages/ContaDetailPage/ContaDetailPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContaDetail, getContas } from '../../services/accountService';
import { getTransacoes, deleteTransacao, reverterTransacaoParaPendente } from '../../services/transactionService';
import { getCategorias } from '../../services/categoryService';
import TransacaoModal from '../../components/TransacaoModal/TransacaoModal';
import SeletorDeMes from '../../components/SeletorDeMes/SeletorDeMes';
import LogoManager from '../../components/LogoManager/LogoManager';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { getErrorMessage } from '../../utils/errorHandler';
import { useDataRefresh } from '../../contexts/DataRefreshContext';

import { Table, Button, Card, Statistic, Tag, Modal, Space, message, Spin, Row, Col, Typography, Divider, Tooltip, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, ExclamationCircleOutlined, RollbackOutlined, CheckCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, BankOutlined, SwapOutlined } from '@ant-design/icons';

import './ContaDetailPage.css';

const { Title, Text } = Typography;

const formatarValor = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarDataISO = (iso) => {
    if (!iso) return '';
    const [ano, mes, dia] = iso.slice(0, 10).split('-');
    return `${dia}/${mes}/${ano}`;
};

const COLUNAS_EXTRATO = 5;

function ContaDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { refreshKey } = useDataRefresh();
    const [conta, setConta] = useState(null);
    const [transacoes, setTransacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTransacao, setEditingTransacao] = useState(null);
    const [todasContas, setTodasContas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [dataFiltro, setDataFiltro] = useState(new Date());
    
    const [modal, modalContextHolder] = Modal.useModal();
    const [messageApi, messageContextHolder] = message.useMessage();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const carregarDadosDaConta = useCallback(async () => {
        try {
            setLoading(true);
            const ano = dataFiltro.getFullYear();
            const mes = dataFiltro.getMonth() + 1;
            const params = { ano, mes, conta: id, extrato: 'true' };

            const [contaData, transacoesData] = await Promise.all([
                getContaDetail(id),
                getTransacoes(params),
            ]);
            setConta(contaData);
            setTransacoes(transacoesData);

        } catch (error) {
            console.error("Falha ao carregar detalhes da conta", error);
            messageApi.error(getErrorMessage(error, "Não foi possível carregar os dados da conta."));
        } finally {
            setLoading(false);
        }
    }, [id, dataFiltro, messageApi]);

    useEffect(() => {
        carregarDadosDaConta();
    }, [carregarDadosDaConta, refreshKey]);

    useEffect(() => {
        const handleTransactionUpdate = () => {
            carregarDadosDaConta();
        };
        window.addEventListener('transaction-updated', handleTransactionUpdate);
        return () => {
            window.removeEventListener('transaction-updated', handleTransactionUpdate);
        };
    }, [carregarDadosDaConta]);

    useEffect(() => {
        getContas().then(data => setTodasContas(data.results || data));
        getCategorias().then(data => setCategorias(data.results || data));
    }, []);
    
    const { totalEntradas, totalSaidas, resultadoDoMes } = useMemo(() => {
        const totais = transacoes.reduce((acc, transacao) => {
            const valor = parseFloat(transacao.valor) || 0;
            if (transacao.status?.toUpperCase() === 'PAGO' || transacao.status?.toUpperCase() === 'RECEBIDO') {
                if (transacao.tipo === 'receita') {
                    acc.totalEntradas += valor;
                } else if (transacao.tipo === 'despesa' || transacao.tipo === 'pagamento_fatura') {
                    acc.totalSaidas += valor;
                }
            }
            return acc;
        }, { totalEntradas: 0, totalSaidas: 0 });

        return {
            ...totais,
            resultadoDoMes: totais.totalEntradas - totais.totalSaidas
        };
    }, [transacoes]);

    // Agrupa o extrato por dia (mantém a ordem que vem do backend: mais recente primeiro).
    // O saldo do dia é o saldo acumulado após a última transação daquele dia — que,
    // na ordem decrescente, é a primeira transação de cada grupo (saldo_apos vem do backend).
    const gruposPorDia = useMemo(() => {
        const grupos = [];
        const mapa = new Map();
        transacoes.forEach((t) => {
            const dia = (t.data_pagamento || t.data_vencimento || '').slice(0, 10);
            if (!mapa.has(dia)) {
                const grupo = { dia, itens: [] };
                mapa.set(dia, grupo);
                grupos.push(grupo);
            }
            mapa.get(dia).itens.push(t);
        });
        grupos.forEach((grupo) => {
            const bruto = grupo.itens[0]?.saldo_apos;
            grupo.saldoDoDia = bruto !== undefined && bruto !== null ? parseFloat(bruto) : null;
        });
        return grupos;
    }, [transacoes]);

    // Lista plana para a tabela: transações + uma linha de "saldo do dia" ao fim de cada grupo.
    const linhasExtrato = useMemo(() => {
        const linhas = [];
        gruposPorDia.forEach((grupo) => {
            grupo.itens.forEach((t) => linhas.push({ ...t, key: t.id, rowKind: 'tx' }));
            if (grupo.saldoDoDia !== null) {
                linhas.push({
                    key: `saldo-dia-${grupo.dia}`,
                    rowKind: 'saldo',
                    dia: grupo.dia,
                    saldoDoDia: grupo.saldoDoDia,
                });
            }
        });
        return linhas;
    }, [gruposPorDia]);


    const handleOpenModal = (transacao = null) => {
        setEditingTransacao(transacao);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingTransacao(null);
        setIsModalOpen(false);
    };

    const handleSave = (shouldClose = true, successMessage = null) => {
        carregarDadosDaConta();
        if (successMessage && typeof successMessage === 'string') {
            messageApi.success(successMessage);
        }
        if (shouldClose) {
            handleCloseModal();
        }
    };

    const handleReverterParaPendente = useCallback(async (transacaoId) => {
        try {
            await reverterTransacaoParaPendente(transacaoId);
            messageApi.success('Transação revertida para pendente!');
            carregarDadosDaConta();
        } catch (error) {
            modal.error({ 
                title: 'Erro', 
                content: getErrorMessage(error, 'Ocorreu um erro ao reverter a transação.') 
            });
        }
    }, [modal, carregarDadosDaConta, messageApi]);


    const handleDelete = useCallback((transacaoId) => {
        modal.confirm({
            title: 'Você tem certeza que deseja excluir esta transação?',
            icon: <ExclamationCircleOutlined />,
            content: 'Esta ação não poderá ser desfeita.',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            async onOk() {
                try {
                    await deleteTransacao(transacaoId);
                    messageApi.success('Transação excluída com sucesso!');
                    carregarDadosDaConta();
                } catch (error) {
                    console.error('Falha ao excluir transação', error);
                    messageApi.error(getErrorMessage(error, 'Erro ao excluir transação.'));
                }
            },
        });
    }, [modal, carregarDadosDaConta, messageApi]);
    
    const getDescricaoTransacao = (transacao) => {
        if (transacao.is_transferencia) {
            const isEntrada = transacao.tipo === 'receita';
            return isEntrada
                ? `Transferência de ${transacao.transacao_espelho_conta_nome || 'outra conta'}`
                : `Transferência para ${transacao.conta_destino_nome || 'outra conta'}`;
        }
        // Descrição já vem com (1/5) do backend para parcelados; opcionalmente exibir tag
        return transacao.descricao;
    }

    // Célula da linha "Saldo do dia": a primeira coluna ocupa a linha inteira; as demais somem.
    const celulaSaldoDoDia = (record, isPrimeira) => {
        if (record.rowKind !== 'saldo') return null;
        if (!isPrimeira) return { children: null, props: { colSpan: 0 } };
        return {
            children: (
                <div className="conta-saldo-dia">
                    <span className="conta-saldo-dia-label">Saldo do dia {formatarDataISO(record.dia)}</span>
                    <span className={record.saldoDoDia < 0 ? 'valor-despesa' : 'valor-receita'}>
                        {formatarValor(record.saldoDoDia)}
                    </span>
                </div>
            ),
            props: { colSpan: COLUNAS_EXTRATO },
        };
    };

    const columns = [
        {
            title: 'Data',
            dataIndex: 'data_pagamento',
            key: 'data',
            render: (dataStr, record) => {
                const saldoCell = celulaSaldoDoDia(record, true);
                if (saldoCell) return saldoCell;
                const isReceita = record.tipo === 'receita';
                const statusText = isReceita ? 'RECEBIDO' : 'PAGO';
                const tagColor = isReceita ? 'blue' : 'green';

                return (
                    <Space>
                        <Tag color={tagColor} icon={<CheckCircleOutlined />}>
                            {statusText}
                        </Tag>
                        <span>{new Date(dataStr || record.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</span>
                    </Space>
                );
            },
        },
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            key: 'descricao',
            render: (text, record) => celulaSaldoDoDia(record, false) || getDescricaoTransacao(record),
        },
        {
            title: 'Categoria',
            dataIndex: 'categoria_nome',
            key: 'categoria_nome',
            render: (text, record) => {
                const saldoCell = celulaSaldoDoDia(record, false);
                if (saldoCell) return saldoCell;
                if (record.tipo === 'pagamento_fatura') {
                    return 'Pagamento de Fatura';
                }
                return text || 'Transferência';
            },
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            align: 'right',
            render: (value, record) => celulaSaldoDoDia(record, false) || (
                <span className={record.tipo === 'receita' ? 'valor-receita' : 'valor-despesa'}>
                    {formatarValor(value)}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'acoes',
            align: 'center',
            render: (_, record) => {
                const saldoCell = celulaSaldoDoDia(record, false);
                if (saldoCell) return saldoCell;
                if (record.tipo === 'pagamento_fatura') {
                    return null;
                }

                const isRevertible = !record.is_transferencia;

                return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Space size="small">
                            {isRevertible ? (
                                <Tooltip title="Reverter para pendente">
                                    <Button
                                        icon={<RollbackOutlined />}
                                        onClick={() => handleReverterParaPendente(record.id)}
                                        type="text"
                                    />
                                </Tooltip>
                            ) : (
                                <span style={{ display: 'inline-block', width: '32px' }}></span>
                            )}
                            <Tooltip title="Editar">
                                <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
                            </Tooltip>
                            <Tooltip title="Excluir">
                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                            </Tooltip>
                        </Space>
                    </div>
                );
            },
        },
    ];

    if (loading && !conta) {
        return (
            <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true">
                <div className="brand-skeleton-line brand-skeleton-line--lg" />
                <div className="brand-skeleton-line brand-skeleton-line--md" />
                <div className="brand-skeleton-line" />
            </div>
        );
    }
    if (!conta) { return <main className="conta-detalhe-container">Conta não encontrada.</main>; }

    return (
        <main 
            className="conta-detalhe-container" 
            style={{ 
                padding: isMobile ? '0' : '0 24px', 
                maxWidth: '100vw',
                overflowX: 'hidden',
                backgroundColor: isMobile ? 'var(--color-background, #F5F0E6)' : 'transparent'
            }}
        >
            {messageContextHolder}
            {modalContextHolder}
            
            {/* --- HEADER --- */}
            <Card 
                className="page-header-card" 
                style={{ 
                    marginBottom: isMobile ? '8px' : '24px', 
                    borderRadius: isMobile ? 0 : 8,
                    border: isMobile ? 'none' : undefined
                }} 
                bodyStyle={{ padding: isMobile ? '12px 16px' : '24px' }}
                bordered={!isMobile}
            >
                <Row justify="space-between" align="middle" gutter={isMobile ? [0, 16] : [16, 16]}>
                    <Col xs={24} md={12}>
                        <Space align="center" size="small" style={{ width: '100%', justifyContent: 'flex-start' }}>
                            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/contas')} />
                            <LogoManager slug={conta?.banco?.slug} type="banco" size={isMobile ? 32 : 40} />
                            <Title level={isMobile ? 5 : 4} style={{ margin: 0, fontSize: isMobile ? '18px' : undefined, lineHeight: '1.2' }}>{conta.nome}</Title>
                        </Space>
                    </Col>
                    
                    <Col xs={24} md={12} style={{ 
                        display: 'flex', 
                        justifyContent: isMobile ? 'space-between' : 'flex-end', 
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 12,
                        width: '100%' 
                    }}>
                        <div style={{ flex: isMobile ? '0 0 auto' : undefined }}>
                            <SeletorDeMes value={dataFiltro} onChange={setDataFiltro} />
                        </div>
                        <Button 
                            key="1" 
                            type="primary" 
                            icon={<PlusOutlined />} 
                            onClick={() => handleOpenModal()} 
                            style={{ flexShrink: 0 }}
                            block={isMobile}
                        >
                            {isMobile ? 'Lançar' : 'Lançamento'}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* --- KPIs: hero de saldo + secundários --- */}
            <div className="conta-kpi-block" style={{ marginBottom: isMobile ? 8 : 24, padding: isMobile ? '0 12px' : 0 }}>
                <Card className="desempenho-hero-card" bordered={false} style={{ marginBottom: 12 }}>
                    <div className="desempenho-hero-label">
                        <BankOutlined />
                        <span>Saldo atual da conta</span>
                    </div>
                    <div className={`desempenho-hero-value${parseFloat(conta.saldo_atual) < 0 ? ' is-negative' : ''}`}>
                        {formatarValor(conta.saldo_atual)}
                    </div>
                </Card>
                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={8}>
                        <Card className="desempenho-kpi-card" bordered={false}>
                            <Statistic
                                title={<span className="conta-kpi-title"><ArrowUpOutlined style={{ color: '#50B0A0' }} /> Entradas no mês</span>}
                                value={totalEntradas}
                                formatter={(value) => formatarValor(value)}
                                valueStyle={{ color: '#2F9E6F', fontVariantNumeric: 'tabular-nums' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card className="desempenho-kpi-card" bordered={false}>
                            <Statistic
                                title={<span className="conta-kpi-title"><ArrowDownOutlined style={{ color: '#D64545' }} /> Saídas no mês</span>}
                                value={totalSaidas}
                                formatter={(value) => formatarValor(value)}
                                valueStyle={{ color: '#D64545', fontVariantNumeric: 'tabular-nums' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card className="desempenho-kpi-card" bordered={false}>
                            <Statistic
                                title={<span className="conta-kpi-title"><SwapOutlined /> Resultado do mês</span>}
                                value={resultadoDoMes}
                                formatter={(value) => formatarValor(value)}
                                valueStyle={{
                                    color: resultadoDoMes >= 0 ? '#2F9E6F' : '#D64545',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
            
            {/* --- EXTRATO --- */}
            <Card 
                title="Extrato da Conta (Transações Pagas)" 
                style={{ borderRadius: isMobile ? 0 : 12, border: isMobile ? 'none' : undefined, margin: isMobile ? '0 12px 12px' : undefined }}
                bodyStyle={{ padding: isMobile ? '0' : '24px' }}
                bordered={!isMobile}
            >
                {isMobile ? (
                    <List
                        dataSource={linhasExtrato}
                        rowKey="key"
                        locale={{
                            emptyText: (
                                <EmptyStateGuide
                                    title="Nenhuma transação paga encontrada."
                                    description="Lance uma receita ou despesa nesta conta para montar o extrato."
                                    actionLabel="Novo lançamento"
                                    onAction={() => handleOpenModal()}
                                />
                            ),
                        }}
                        renderItem={(item) => (
                            item.rowKind === 'saldo' ? (
                                <div className="conta-saldo-dia conta-saldo-dia--mobile">
                                    <span className="conta-saldo-dia-label">Saldo do dia {formatarDataISO(item.dia)}</span>
                                    <span className={item.saldoDoDia < 0 ? 'valor-despesa' : 'valor-receita'}>
                                        {formatarValor(item.saldoDoDia)}
                                    </span>
                                </div>
                            ) : (
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
                                <Row justify="space-between" align="top" style={{ marginBottom: 4 }}>
                                    <Col span={16}>
                                        <Text strong style={{ fontSize: 15, display: 'block', lineHeight: 1.2 }}>{getDescricaoTransacao(item)}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{item.categoria_nome || 'Transferência'}</Text>
                                    </Col>
                                    <Col span={8} style={{ textAlign: 'right' }}>
                                        <Text strong style={{ color: item.tipo === 'receita' ? '#2F9E6F' : '#D64545', fontSize: 15 }}>
                                            {formatarValor(item.valor)}
                                        </Text>
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                            {new Date(item.data_pagamento || item.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                                        </div>
                                    </Col>
                                </Row>
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                    <Tag color={item.tipo === 'receita' ? 'blue' : 'green'} style={{ marginRight: 0, fontSize: 10, lineHeight: '18px' }}>
                                        {item.tipo === 'receita' ? 'RECEBIDO' : 'PAGO'}
                                    </Tag>
                                    <Space size="small">
                                        {!item.is_transferencia && item.tipo !== 'pagamento_fatura' && (
                                            <Button icon={<RollbackOutlined />} onClick={() => handleReverterParaPendente(item.id)} size="small" type="text" />
                                        )}
                                        {item.tipo !== 'pagamento_fatura' && (
                                            <>
                                                <Button icon={<EditOutlined />} onClick={() => handleOpenModal(item)} size="small" type="text" />
                                                <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(item.id)} size="small" type="text" />
                                            </>
                                        )}
                                    </Space>
                                </div>
                            </div>
                            )
                        )}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={linhasExtrato}
                        loading={loading}
                        rowClassName={(record) => (
                            record.rowKind === 'saldo'
                                ? 'conta-saldo-dia-row'
                                : `status-${record.status?.toLowerCase()}`
                        )}
                        pagination={linhasExtrato.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
                        locale={{
                            emptyText: (
                                <EmptyStateGuide
                                    title="Nenhuma transação neste período."
                                    description="Ajuste o mês ou registre um lançamento nesta conta."
                                    actionLabel="Novo lançamento"
                                    onAction={() => handleOpenModal()}
                                />
                            ),
                        }}
                    />
                )}
            </Card>
            
            <TransacaoModal 
                isOpen={isModalOpen} 
                onClose={handleCloseModal} 
                onSave={handleSave} 
                transacaoToEdit={editingTransacao} 
                contas={todasContas} 
                allCategorias={categorias}
                isMobile={isMobile}
            />
        </main>
    );
}

export default ContaDetailPage;